export const isInlayOnlayEligible = (toothNumber: number): boolean => {
  const eligibleTeeth = [
    // Upper teeth (1-16)
    1,
    2,
    3,
    4,
    5, // Upper right molars and premolars
    12,
    13,
    14,
    15,
    16, // Upper left molars and premolars

    // Lower teeth (17-32)
    17,
    18,
    19,
    20,
    21, // Lower left molars and premolars
    28,
    29,
    30,
    31,
    32, // Lower right molars and premolars
  ]

  return eligibleTeeth.includes(toothNumber)
}

export const isSealantsEligible = (toothNumber: number): boolean => {
  return toothNumber >= 1 && toothNumber <= 32
}

export const getInlayOnlayEligibilityReason = (toothNumber: number): string => {
  if (isInlayOnlayEligible(toothNumber)) {
    return `Tooth ${toothNumber} is eligible for inlay/onlay treatment.`
  }

  const anteriorTeeth = [6, 7, 8, 9, 10, 11, 22, 23, 24, 25, 26, 27]

  if (anteriorTeeth.includes(toothNumber)) {
    return `Inlays/Onlays are not applicable to Tooth ${toothNumber}. Inlays and onlays are only applicable to posterior teeth (molars and premolars): Teeth 1-5, 12-16, 17-21, and 28-32.`
  }

  return `Tooth ${toothNumber} is not eligible for inlay/onlay treatment. Inlays and onlays are only applicable to posterior teeth (molars and premolars): Teeth 1-5, 12-16, 17-21, and 28-32.`
}

export const getSealantsEligibilityReason = (toothNumber: number): string => {
  if (isSealantsEligible(toothNumber)) {
    return `Tooth ${toothNumber} is eligible for sealants treatment.`
  }

  return `Tooth ${toothNumber} is not eligible for sealants treatment. Use a valid permanent tooth number (1–32).`
}

export const getInlayOnlayEligibleTeeth = (): number[] => {
  return [
    // Upper teeth (1-16)
    1,
    2,
    3,
    4,
    5, // Upper right molars and premolars
    12,
    13,
    14,
    15,
    16, // Upper left molars and premolars

    // Lower teeth (17-32)
    17,
    18,
    19,
    20,
    21, // Lower left molars and premolars
    28,
    29,
    30,
    31,
    32, // Lower right molars and premolars
  ]
}

export const getSealantsEligibleTeeth = (): number[] => {
  return Array.from({ length: 32 }, (_, index) => index + 1)
}

export const getInlayOnlayEligibilityMessage = (): string => {
  return 'Inlays and Onlays are only applicable to posterior teeth (molars and premolars): Teeth 1-5, 12-16, 17-21, and 28-32.'
}

export const getSealantsEligibilityMessage = (): string => {
  return 'Sealants may be applied to any permanent tooth (1–32), including anterior teeth.'
}

export const getInlayOnlayEligibilityClass = (toothNumber: number): string => {
  if (isInlayOnlayEligible(toothNumber)) {
    return 'inlay-eligible'
  }
  return 'inlay-ineligible'
}

export const getSealantsEligibilityClass = (toothNumber: number): string => {
  if (isSealantsEligible(toothNumber)) {
    return 'sealants-eligible'
  }
  return 'sealants-ineligible'
}

export const getInlayOnlayTooltip = (toothNumber: number): string => {
  if (isInlayOnlayEligible(toothNumber)) {
    return `Tooth ${toothNumber}: Eligible for inlay/onlay treatment`
  }
  return `Tooth ${toothNumber}: Not eligible for inlay/onlay treatment. Only applicable to posterior teeth (1-5, 12-16, 17-21, 28-32).`
}

export const getSealantsTooltip = (toothNumber: number): string => {
  if (isSealantsEligible(toothNumber)) {
    return `Tooth ${toothNumber}: Eligible for sealants treatment`
  }
  return `Tooth ${toothNumber}: Not eligible for sealants treatment. Use a valid permanent tooth number (1–32).`
}

const TOOTH_WIDTH = 56
const MOLAR_WIDTH = 61
const SPECIAL_MOLAR_WIDTH = 59

// Teeth with 63px width: 1, 2, 15, 16, 17, 18, 31, 32
const SPECIAL_MOLAR_IDS = [1, 2, 15, 16, 17, 18, 31, 32]

// Remaining molar teeth with 65px width: 3, 14, 19, 30
const MOLAR_IDS = [3, 14, 19, 30]

export const getToothWidth = (toothId: number): number => {
  if (SPECIAL_MOLAR_IDS.includes(toothId)) {
    return SPECIAL_MOLAR_WIDTH
  }
  if (MOLAR_IDS.includes(toothId)) {
    return MOLAR_WIDTH
  }
  return TOOTH_WIDTH
}
